import { request as http } from '@/utils/http'
import type { PageRequest } from '@/types/api'

// API基础路径
const API_BASE = '/mdm/exchangeRateSetting'

// ==================== 通用类型定义 ====================

// 分页响应类型
export interface PageResultVO<T> {
  pageNum: number // 当前页码
  pageSize: number // 每页大小
  total: number // 总记录数
  pages: number // 总页数
  records: T[] // 数据列表
}

// ==================== 汇率设置相关类型定义 ====================

// 汇率设置分页请求
export interface ExchangeRateSettingQueryRequest extends PageRequest {
  fromCurrencyCode?: string // 币种代码，如 USD
  toCurrencyCode?: string // 本位币代码，如 CNY
  exchangeRateMin?: number // 汇率最小值
  exchangeRateMax?: number // 汇率最大值
  effectiveDateStartFrom?: string // 有效期开始日期范围-开始
  effectiveDateStartTo?: string // 有效期开始日期范围-结束
  effectiveDateEndFrom?: string // 有效期结束日期范围-开始
  effectiveDateEndTo?: string // 有效期结束日期范围-结束
  statusCode?: string // 状态代码：enabled-启用, disabled-禁用
  tenantId?: string // 租户ID
  effectiveDate?: string // 查询指定日期有效的汇率
  currencyPair?: string // 币种对查询，格式如 "USD/CNY"
}

// 汇率设置视图对象
export interface ExchangeRateSettingVO {
  id: string // 主键ID
  fromCurrencyCode: string // 币种代码，如 USD
  toCurrencyCode: string // 本位币代码，如 CNY
  exchangeRate: number // 汇率，如 7.2500
  effectiveDateStart: string // 有效期开始日期，如 2024-01-01
  effectiveDateEnd: string // 有效期结束日期，如 2024-12-31
  statusCode: string // 状态代码：enabled-启用, disabled-禁用
  dateCreated?: string // 创建时间
  dateModified?: string // 更新时间
  createdBy?: string // 创建人ID
  modifiedBy?: string // 修改人ID
  version?: number // 版本号
  tenantId?: string // 租户ID
}

// 汇率设置保存请求
export interface ExchangeRateSettingSaveRequest {
  id?: string // 主键ID（更新时需要）
  fromCurrencyCode: string // 币种代码，如 USD
  toCurrencyCode: string // 本位币代码，如 CNY
  exchangeRate: number // 汇率，如 7.2500
  effectiveDateStart: string // 有效期开始日期，如 2024-01-01
  effectiveDateEnd: string // 有效期结束日期，如 2024-12-31
  statusCode?: string // 状态代码：enabled-启用, disabled-禁用
  tenantId?: string // 租户ID
}

// ==================== API客户端对象 ====================

// 汇率设置API
export const exchangeRateSettingApi = {
  // 分页查询汇率设置
  page: (params: ExchangeRateSettingQueryRequest) => {
    return http.post<PageResultVO<ExchangeRateSettingVO>>(`${API_BASE}/queryPage`, params)
  },

  // 查询汇率设置列表
  list: (params: ExchangeRateSettingQueryRequest) => {
    return http.post<ExchangeRateSettingVO[]>(`${API_BASE}/queryList`, params)
  },

  // 获取汇率设置详情
  get: (id: string) => {
    return http.get<ExchangeRateSettingVO>(`${API_BASE}/getById`, { params: { id } })
  },

  // 保存汇率设置（新增或更新）
  save: (params: ExchangeRateSettingSaveRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 删除汇率设置
  delete: (id: string) => {
    return http.delete<boolean>(`${API_BASE}/delete`, { params: { id } })
  },

  // 批量删除汇率设置
  batchDelete: (ids: string[]) => {
    return http.delete<boolean>(`${API_BASE}/batchDelete`, { data: ids })
  },

  // 根据币种对查询汇率设置
  getByCurrencyPair: (fromCurrencyCode: string, toCurrencyCode: string, tenantId?: string) => {
    const params: any = { fromCurrencyCode, toCurrencyCode }
    if (tenantId !== undefined) {
      params.tenantId = tenantId
    }
    return http.post<ExchangeRateSettingVO[]>(`${API_BASE}/getByCurrencyPair`, null, { params })
  },

  // 获取指定日期有效的汇率
  getEffectiveRate: (fromCurrencyCode: string, toCurrencyCode: string, effectiveDate: string, tenantId?: string) => {
    const params: any = { fromCurrencyCode, toCurrencyCode, effectiveDate }
    if (tenantId !== undefined) {
      params.tenantId = tenantId
    }
    return http.post<ExchangeRateSettingVO>(`${API_BASE}/getEffectiveRate`, null, { params })
  },

  // 模糊搜索汇率设置
  search: (keyword: string, fromCurrencyCode?: string, toCurrencyCode?: string, statusCode?: string, tenantId?: string) => {
    const params: any = { keyword }
    if (fromCurrencyCode) params.fromCurrencyCode = fromCurrencyCode
    if (toCurrencyCode) params.toCurrencyCode = toCurrencyCode
    if (statusCode) params.statusCode = statusCode
    if (tenantId !== undefined) params.tenantId = tenantId
    return http.post<ExchangeRateSettingVO[]>(`${API_BASE}/search`, null, { params })
  },

  // 启用汇率设置
  enable: (id: string) => {
    return http.post<boolean>(`${API_BASE}/enable`, null, { params: { id } })
  },

  // 禁用汇率设置
  disable: (id: string) => {
    return http.post<boolean>(`${API_BASE}/disable`, null, { params: { id } })
  },

  // 批量启用汇率设置
  batchEnable: (ids: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchEnable`, ids)
  },

  // 批量禁用汇率设置
  batchDisable: (ids: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchDisable`, ids)
  },

  // 批量更新状态
  updateStatus: (ids: string[], statusCode: string) => {
    return http.post<boolean>(`${API_BASE}/updateStatus`, ids, { params: { statusCode } })
  }


}
